
import { css } from '@emotion/css';
import SsmlField from '../../../components/forms/SsmlField/SsmlField';
import _ from 'lodash';
import ResponseGroup from '../../../models/features/ResponseGroup';
import { color_colors_decline, color_colors_ocean, color_gradients_silver_shift, color_shades_darker, color_shades_darkest, color_text_default, color_text_link, color_variants_ocean_light_opaque, color_variants_red_2, dark_red } from '../../../constants/colors';
import { style_border_default } from '../../../constants/stylesValues';
import styled from '@emotion/styled';
import TextFieldSubtitle from '../../../components/forms/TextFieldSubtitle';
import TextAreaField from '../../../components/forms/TextAreaField';
import TextField from '../../../components/forms/TextField';
import Switch from '../../../components/forms/Switch';
import HorizontalOptionsSelector from '../../../components/forms/HorizontalOptionsSelector';
import IconTooltip from '../../../components/general/IconTooltip';
import { GenAIModels } from '../../../constants/genAiModels';
import StyledDropdown from '../../applicationSettings/components/customAssistantConfigurations/StyledDropdown';
import React, { Fragment, useEffect } from 'react';
import ResponseInstanceModel from '../../../models/features/api/ResponseInstanceModel';
import ResponsesContainer from '../../../hooks/ResponsesContainer';
import set from 'lodash';
import ResponseChooser from './ResponseChooser';

const temperatureOptions = [
    { name: '.1', value: .1 },
    { name: '.2', value: .2 },
    { name: '.3', value: .3 },
    { name: '.4', value: .4 },
    { name: '.5', value: .5 },
    { name: '.6', value: .6 },
    { name: '.7', value: .7 },
    { name: '.8', value: .8 },
    { name: '.9', value: .9 },
    { name: '1', value: 1 },
];

interface ResponseProps {
    index: number;
    answer: ResponseInstanceModel;
    handleTitleChange: (newTitle: string) => void;
    handleRemoveVariation: () => void;
    handleCopyVariation: () => void;
    groupIndex: number;
    group: ResponseGroup;
    isDisabled: boolean;
    onBlur: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    handleSystemPromptUpdate: (value: string) => void;
    handleContextualSystemPrompt: (value: string) => void;
    applicationId: string;
    handleToggleExcludeConversationHistoryForGenAI: () => void;
    handleConversationTurnsLimitUpdate: (value: string,) => void;
    handleSsmlUpdate: (value: string) => void;
    handleTemperature: (value: number) => void;
    handleModelChange: (value: string) => void;
    handleToggleModelOverride: () => void;
    handleUpdateFullResponse: (response: ResponseInstanceModel) => void;
    handleToggleUseHistoryInSystemPrompt: () => void;
    handleToggleOnlyIncludeCurrentContentItemHistory: () => void;
    showError: (error: string) => void
}
const Response: React.FC<ResponseProps> = (props) => {
    const responseContainer = ResponsesContainer.useContainer();
    const [showChooser, setShowChooser] = React.useState(false);
    const [responseTitle, setResponseTitle] = React.useState<string>(props.answer?.name);
    const [showUsage, setShowUsage] = React.useState(false);

    useEffect(() => {
        if (props.applicationId?.length && props.groupIndex === 0 && props.index === 0) {
            responseContainer.loadApplicationResponses(props.applicationId);
        }
    }, [props.applicationId, props.groupIndex, props.index]);
    useEffect(() => {
        if (props?.answer?.responseId?.length) {
            responseContainer.loadResponse(props.answer.responseId);
        }
    }, [props?.answer?.responseId])

    const clickLabel = () => {
        setShowUsage(true);
        setShowChooser(true);
    }

    const chooseResponse = async (responseId: string) => {
        const chosenResponse = await responseContainer.loadResponse(responseId, true);
        if (chosenResponse) {
            const newResponse = {
                ...props.answer,
                ...chosenResponse,
            }
            newResponse.responseId = chosenResponse.id;
            newResponse.id = undefined;
            props.handleUpdateFullResponse(newResponse);
            setResponseTitle(chosenResponse.name);
        }
    }

    const handleTitleBlur = () => {
        const existingResponse = responseContainer.responses.find((r) => props.answer?.responseId?.length && r?.id !== props.answer?.responseId && r?.name?.length && r?.name === responseTitle);
        if (existingResponse) {
            props.showError(`A response with the name "${responseTitle}" already exists. Please choose a different name.`);
            setResponseTitle(null);
        } else {
            props.handleTitleChange(responseTitle);
        }
    }
    let usedInArr = responseContainer.responseInfos.find((r) => r?.id === props.answer?.responseId)?.usedIn;
    usedInArr = usedInArr?.filter((u) => u?.contentItemId?.length);
    const usedIn = usedInArr?.length ?? 0;
    return (
        <Fragment key={"a" + props.index}>
            <TextFieldSubtitle
                titleText={responseTitle ?? `Variation #${props.index + 1}`}
                canCopy
                canSelect={props.group?.responses?.length < 2}
                editableTitle={props.group?.responses?.length < 2}
                canDelete={props.index > 0}
                onChangeTitle={(newTitle) => setResponseTitle(newTitle)}
                titlePlaceholder={`Variation #${props.index + 1}`}
                onDelete={() => props.handleRemoveVariation()}
                onSelect={() => { setShowUsage(false); setShowChooser(!showChooser); }}
                onCopy={() => props.handleCopyVariation()}
                onTitleBlur={() => {
                    handleTitleBlur();
                }}
                selectCount={usedIn}
            />
            <HolderDiv key={"s" + props.index}>
                {props.answer.useEnhancedResponse ?
                    <>
                        <TextAreaField
                            id={`g${props.groupIndex}systemPrompt${props.index}`}
                            disabled={props.isDisabled}
                            name={`responses.${props.index}.systemPrompt`}
                            value={props.answer?.systemPrompt ?? ""}
                            placeholder="A system prompt for this response."
                            label={`System Prompt`}
                            tooltip='A system level prompt given to the generative AI model<br />
                                            that describes how it should respond.<br />
                                            This is optional and added before the conversation history.'
                            onChange={(e) => props.handleSystemPromptUpdate(e.target.value)}
                            onBlur={props.onBlur}
                            className="ac-response-ssml" />

                        <TextAreaField
                            id={`g${props.groupIndex}contextualSystemPrompt${props.index}`}
                            disabled={props.isDisabled}
                            name={`responses.${props.index}.contextualSystemPrompt`}
                            value={props.answer?.contextualSystemPrompt ?? ""}
                            placeholder="A prompt given to the Generative AI right before this turn of the conversation."
                            label={`Contextual System Prompt`}
                            tooltip='A system prompt given to the generative AI model<br />
                                            right before the most recent turn of the conversation.<br />
                                            Can be more specific for the current conversation turn.'
                            onChange={(e) => props.handleContextualSystemPrompt(e.target.value)}
                            onBlur={props.onBlur}
                            className="ac-response-ssml" />

                        <SsmlField
                            id={`g${props.groupIndex}response${props.index}`}
                            applicationId={props.applicationId}
                            disabled={props.isDisabled}
                            name={`responses.${props.index}.content`}
                            value={props.answer.content}
                            placeholder="A Phrase"
                            labelColor={usedIn > 1 ? dark_red : undefined}
                            clickableLabel={usedIn > 1}
                            onClickLabel={usedIn > 1 ? () => clickLabel() : undefined}
                            label={usedIn > 1 ? `Response used in ${usedIn} places!` : props.answer.useEnhancedResponse ? `Backup Response` : `Response`}
                            onChange={(value) => props.handleSsmlUpdate(value)}
                            onBlur={props.onBlur}
                            className="ac-response-ssml" />
                        <>
                            <TempLabel>
                                <TempDescription>Temperature</TempDescription>
                                <IconTooltip text='Higher temperatures result in more creative responses<br />at the risk of less consistent and controlled output.' />
                            </TempLabel>
                            <HorizontalOptionsSelector
                                name={`responses.${props.index}.temperature`}
                                options={temperatureOptions}
                                value={props.answer.temperature ?? .1}
                                isCustom={false}
                                onChange={(value) => {
                                    props.handleTemperature(value);
                                }}
                            />

                        </>


                        <ModelOverrideSwitch
                            label="Include Conversation History"
                            checked={!props.answer?.excludeConversationHistoryForGenAI}
                            onChange={() => props.handleToggleExcludeConversationHistoryForGenAI()}
                        />
                        {
                            !props.answer?.excludeConversationHistoryForGenAI &&
                            <ConversationTurnsLimitWrapper>
                                <TextField
                                    id={`g${props.groupIndex}conversationTurnsLimit${props.index}`}
                                    disabled={props.isDisabled || props.answer.excludeConversationHistoryForGenAI == true}
                                    name={`responses.${props.index}.lastTurnsToInclude`}
                                    value={(props.answer?.lastTurnsToInclude !== undefined && props.answer?.lastTurnsToInclude !== 0) ? props.answer.lastTurnsToInclude.toString() : "10"}
                                    placeholder="Enter a number"
                                    label={`Limit to last N conversation turns`}
                                    tooltip='Limits the conversation history to the specified number of most recent turns. Leave empty to use the entire conversation.'
                                    onChange={(e) => props.handleConversationTurnsLimitUpdate(e.target.value)}
                                    onBlur={props.onBlur}
                                    className="ac-response-ssml"
                                    type="number"
                                />
                            </ConversationTurnsLimitWrapper>
                        }
                        <ModelOverrideSwitch
                            label="Enable Model Override"
                            checked={props.answer?.useModelOverride || (props.answer?.generativeAIModelOverride != null)}
                            onChange={() => props.handleToggleModelOverride()}
                        />
                        {(props.answer?.useModelOverride || (props.answer?.generativeAIModelOverride != null)) && (
                            <SquareStyledDropdown
                                className="ac-response-ssml"
                                fieldName="generativeResponseModel"
                                label="Generative AI Model Override"
                                labelStyle={leftMarginLabelStyle}
                                handleOnChange={(_, value) => props.handleModelChange(value as string)}
                                value={props.answer?.generativeAIModelOverride}
                                searchable={false}
                                placeholder="Select AI Model"
                                options={GenAIModels}
                            />
                        )}
                        <ModelOverrideSwitch
                            label="Use History in System Prompt"
                            checked={props.answer?.useHistoryInSystemPrompt}
                            onChange={() => props.handleToggleUseHistoryInSystemPrompt()}
                        />
                        <ModelOverrideSwitch
                            label="Only Include Current Content Item History"
                            checked={props.answer?.onlyIncludeCurrentContentItemHistory}
                            onChange={() => props.handleToggleOnlyIncludeCurrentContentItemHistory()}
                        />
                    </>
                    :
                    <SsmlField
                        id={`g${props.groupIndex}response${props.index}`}
                        applicationId={props.applicationId}
                        disabled={props.isDisabled}
                        name={`responses.${props.index}.content`}
                        value={props.answer.content}
                        placeholder="A Phrase"
                        labelColor={usedIn > 1 ? dark_red : undefined}
                        clickableLabel={usedIn > 1}
                        onClickLabel={usedIn > 1 ? () => clickLabel() : undefined}
                        label={usedIn > 1 ? `Response used in ${usedIn} places!` : props.answer.useEnhancedResponse ? `Backup Response` : `Response`}
                        onChange={(value) => props.handleSsmlUpdate(value)}
                        onBlur={props.onBlur}
                        className="ac-response-ssml" />
                }
                {showChooser ? <ResponseChooser
                    currentResponseId={props.answer?.responseId}
                    showUsage={showUsage}
                    chooseResponse={(responseId) => {
                        chooseResponse(responseId);
                        setShowChooser(false);
                    }} appId={props.applicationId} /> : <></>}
            </HolderDiv>
        </Fragment>
    )
}

const HolderDiv = styled.div`
    position: relative;
`;
const TempLabel = styled.div`
    display: flex;
    flex-direction: row;
`
const TempDescription = styled.p`
    margin-bottom: 16px;
    margin-left: 16px;
    font-size: 14px;
    font-weight: 600;
    text-align: left;
    margin-right: 8px;
`

const leftMarginLabelStyle = css`
    margin-left: 16px;
    font-weight: 600;
`;

const SquareStyledDropdown = styled(StyledDropdown)`
    .Select-control {
        border-radius: 4px !important;
        height: 48px !important;
    }
    .Select-menu-outer {
        border-radius: 4px !important;
    }
    .Select-placeholder {
        line-height: 48px !important;
    }
    .Select-value {
        line-height: 48px !important;
    }
    margin-bottom: 18px;
    margin-left: 18px;
`;

const ModelOverrideSwitch = styled(Switch)`
    padding-top: 8px !important;
    margin-bottom: 16px;
`;

const ConversationTurnsLimitWrapper = styled.div`
    margin-left: 10px;
`;
export default Response;